import {
  Component,
  OnInit,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnChanges,
  AfterViewInit,
  ViewEncapsulation,
} from '@angular/core';
import { NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';

/**
### Official Bug in DatePicker
 *
 *  MinDate will not dynamically update when dynamic binding, you need to close and re-open the calendar
 *  by force in order to see new values.
 *  This component will not handle that, the component in which you will use this will open or close it
 *  to update using the calendar reference.
 *
#### Bug Issued Forum for DatePicker
 *
 *  https://github.com/valor-software/ngx-bootstrap/issues/5888
 *
 *
 * #### Calendar Mode
 *
 * - ToggleButton = True
 *
 *  This will show an input field and an icon to toggle the calendar
 *
 *
 * - ToggleButton = False
 *
 *  This will show the calendar (opened) but you cannot close it.
 */

@Component({
  selector: 'cygov-ngb-calendar',
  templateUrl: './ngb-calendar.component.html',
  styleUrls: ['./ngb-calendar.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class NgbCalendarComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() selectedDate: NgbDateStruct = null; // The current selected Date
  @Input() isCalendarDisabled = false; // Option to disable the calendar
  @Input() disableFullOpacity = false;
  @Input() dateToShow: NgbDateStruct = null; // To show date on top of calendar, only seen in non-toggle mode
  @Input() dateType: string; // An ID for the calendar
  @Input() inputMaxDate: NgbDateStruct; // minimum selection date on calendar, not passing this value will not set the minDate
  @Input() inputMinDate: NgbDateStruct; // maximum selection date on calendar, not passing this value will not set the maxDate
  @Input() toggleButton = false; // Toggle the icon and calendar mode (open or close).
  @Input() placeHolder = ''; // Placeholder to show on input field, only visible in Toggle Mode
  @Input() displayInputArea = false; // To display Input area of date picker
  @Input() openDefault = false;
  @Input() navigation = 'select';
  @Input() isReadOnly: boolean = false;
  @Input() showDateLabel: boolean = false;
  @Input() disableDates = {};
  @Input() firstDayOfWeek = 1;
  @Input() isFromVendor = false;
  @Input() saveSelectedDate = false;
  @Input() riskLineChartData: any[] = [];
  @Input() isFromRiskRegister = false;
  @Input() isFromPillarChart: boolean = false;
  @Output() monthChanged: EventEmitter<any> = new EventEmitter<any>();
  // Trigger to return the new selected date if any
  @Output() selectedDateChange: EventEmitter<NgbDateStruct> = new EventEmitter<NgbDateStruct>();
  // Pass the calendar ref to parent - optional
  @Output() calendarRef: EventEmitter<NgbInputDatepicker> = new EventEmitter<NgbInputDatepicker>();
  @Output() isCalendarOpen: EventEmitter<boolean> = new EventEmitter<boolean>();
  // A reference to the calendar
  @ViewChild('dateInput') dateCalendar: NgbInputDatepicker;

  loaded = false;
  // code for calendar customization (select month and year)
  yearsList: any = [];
  yearsRange: number = 10;
  monthsList: any = [];
  allMonthsList: any;
  currentSelectedYear: any;
  currentSelectedMonth: any;
  baseYear: string;

  constructor() {}

  ngOnChanges(): void {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    // emit the calendar ref access internal functions
    setTimeout(() => {
      this.calendarRef.emit(this.dateCalendar);
      if (this.openDefault) {
        this.dateCalendar.open();
      }
      this.loaded = true;
    }, 100);
  }

  getDateInDateFormat(date: NgbDateStruct): Date {
    return new Date(date.year, date.month - 1, date.day);
  }

  dateUpdated(date: NgbDateStruct): void {
    // emit the new date selected
    this.selectedDateChange.emit(date);
    this.calendarToggle();
  }

  isDataFound(date: NgbDateStruct): boolean {
    if (!date || !this.isFromRiskRegister || !this.riskLineChartData?.length) {
      return false;
    }

    for (const data of this.riskLineChartData) {
      const createdAtDate = new Date(data.createdAt);
      if (
        createdAtDate.getFullYear() === date.year &&
        createdAtDate.getMonth() + 1 === date.month &&
        createdAtDate.getDate() === date.day
      ) {
        return true;
      }
    }
    return false;
  }

  getDivStyles(day) {
    day = day < 10 ? `0${day}` : day;
    const styles = {
      opacity: this.disableDates[day] ? '0.5' : '',
      'pointer-events': this.disableDates[day] ? 'none' : '',
      cursor: this.disableDates[day] ? 'not-allowed' : '',
    };
    return styles;
  }

  calendarToggle() {
    if (this.toggleButton) {
      this.dateCalendar.toggle();
      this.isCalendarOpen.emit(this.dateCalendar.isOpen());
    }
  }
}
